import { useEffect, useState } from 'react';
import api from '../../utils/api';
import * as S from './styles';

/*
 * Storage and preservation: how the specimen is kept, under which protocol,
 * where exactly (unit, rack, box, well), and how many copies are left.
 */
// routes/specimens.py's PRESERVATION_METHODS; "dried" since 0055 (a dried voucher, not freeze-dried);
// "glycerol_30" since 0064, in place of "mineral_oil" (the lab keeps cultures in 30% glycerol).
export const PRESERVATION_METHODS = ['cryopreservation', 'glycerol_30', 'slant', 'freeze_drying', 'water',
  'silica_gel', 'dried', 'other'];
export const STORAGE_UNITS = ['freezer_minus_80', 'freezer_minus_20', 'refrigerator_4', 'room_temp',
  'liquid_nitrogen', 'other'];
const FIELDS = ['preservation_method', 'protocol_id', 'storage_unit', 'storage_unit_label', 'storage_rack',
  'storage_box', 'storage_well', 'copies_count'];

const formOf = (s) => Object.fromEntries(FIELDS.map((f) => [f, s[f] ?? (f === 'copies_count' ? 0 : '')]));

/** "Freezer -80 °C · Freezer B · rack 3 · box 12 · well C7", or ''. */
export function describeLocation(s, t) {
  const parts = [];
  if (s.storage_unit) parts.push(t(`storage.units.${s.storage_unit}`));
  if (s.storage_unit_label) parts.push(s.storage_unit_label);
  if (s.storage_rack) parts.push(t('storage.rackN', { n: s.storage_rack }));
  if (s.storage_box) parts.push(t('storage.boxN', { n: s.storage_box }));
  if (s.storage_well) parts.push(t('storage.wellN', { n: s.storage_well }));
  return parts.join(' · ');
}

/** "Freezer B / 3 / 12 / C7", short enough for a table cell -- a slant's or a
 *  DNA tube's place as utils.storage_location_text writes it, except that the
 *  unit's name is translated when no Freezer ID names it. '' outside a unit. */
export function shortLocation(s, t) {
  if (!s.storage_unit) return '';
  return [s.storage_unit_label || t(`storage.units.${s.storage_unit}`), s.storage_rack, s.storage_box,
    s.storage_well].filter(Boolean).join(' / ');
}

export default function StoragePanel({ specimen, canEdit, busy, error, onSave, t }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => formOf(specimen));
  const [protocols, setProtocols] = useState([]);
  useEffect(() => { setForm(formOf(specimen)); }, [specimen]);
  useEffect(() => {
    if (!editing) return;
    api.get('/protocols').then((res) => setProtocols(res.data || [])).catch(() => setProtocols([]));
  }, [editing]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const body = {};
    FIELDS.forEach((f) => {
      const value = f === 'copies_count' ? Number(form[f] || 0) : (form[f] || null);
      if (value !== (specimen[f] ?? (f === 'copies_count' ? 0 : null))) body[f] = value;
    });
    if (Object.keys(body).length === 0) { setEditing(false); return; }
    if (await onSave(body)) setEditing(false);
  };

  const location = describeLocation(specimen, t);
  return (
    <section style={S.card} aria-labelledby="storage-title" data-testid="storage-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 id="storage-title" style={S.sectionTitle}>{t('storage.title')}</h3>
        {canEdit && !editing && (
          <button type="button" style={S.ghostBtn} onClick={() => setEditing(true)} title={t('storage.tip.edit')}
            data-testid="storage-edit">{t('storage.edit')}</button>
        )}
      </div>
      {!editing && (
        <dl style={S.dl}>
          <dt style={S.dt}>{t('storage.method')}</dt>
          <dd style={S.dd}>{specimen.preservation_method ? t(`storage.methods.${specimen.preservation_method}`) : '—'}</dd>
          <dt style={S.dt}>{t('storage.protocol')}</dt>
          <dd style={S.dd}>{specimen.protocol_name || '—'}</dd>
          <dt style={S.dt}>{t('storage.location')}</dt>
          <dd style={S.dd} data-testid="storage-location">{location || '—'}</dd>
          <dt style={S.dt}>{t('storage.copiesLabel')}</dt>
          <dd style={S.dd}>
            <span style={{ fontWeight: 700, color: specimen.copies_count ? 'var(--c-text-fg)' : 'var(--c-danger-fg)' }}>
              {specimen.copies_count}
            </span>
            {!specimen.copies_count && specimen.preservation_method && <span style={S.hint}> · {t('storage.noneLeft')}</span>}
          </dd>
        </dl>
      )}
      {editing && (
        <form onSubmit={save}>
          <div style={S.row}>
            <label style={{ ...S.label, flex: '1 1 180px' }}>
              {t('storage.method')}
              <select value={form.preservation_method} onChange={set('preservation_method')} style={S.input}
                title={t('storage.tip.method')} data-testid="storage-method">
                <option value="">—</option>
                {PRESERVATION_METHODS.map((m) => <option key={m} value={m}>{t(`storage.methods.${m}`)}</option>)}
              </select>
            </label>
            <label style={{ ...S.label, flex: '1 1 180px' }}>
              {t('storage.protocol')}
              <select value={form.protocol_id} onChange={set('protocol_id')} style={S.input}
                title={t('storage.tip.protocol')} data-testid="storage-protocol">
                <option value="">—</option>
                {form.protocol_id && !protocols.some((p) => p.protocol_id === form.protocol_id) && (
                  <option value={form.protocol_id}>{specimen.protocol_name || form.protocol_id}</option>
                )}
                {protocols.map((p) => <option key={p.protocol_id} value={p.protocol_id}>{p.name}</option>)}
              </select>
            </label>
            <label style={{ ...S.label, flex: '1 1 180px' }}>
              {t('storage.unit')}
              <select value={form.storage_unit} onChange={set('storage_unit')} style={S.input}
                title={t('storage.tip.unit')} data-testid="storage-unit">
                <option value="">—</option>
                {STORAGE_UNITS.map((u) => <option key={u} value={u}>{t(`storage.units.${u}`)}</option>)}
              </select>
            </label>
          </div>
          <div style={{ ...S.row, marginTop: 10 }}>
            <label style={{ ...S.label, flex: '2 1 160px' }}>
              {t('storage.unitLabel')}
              <input value={form.storage_unit_label} onChange={set('storage_unit_label')} maxLength={60} style={S.input}
                placeholder={t('storage.unitLabelPlaceholder')} title={t('storage.tip.unitLabel')} />
            </label>
            {['storage_rack', 'storage_box', 'storage_well'].map((f) => (
              <label key={f} style={{ ...S.label, flex: '1 1 80px' }}>
                {t(`storage.fields.${f}`)}
                <input value={form[f]} onChange={set(f)} maxLength={f === 'storage_well' ? 5 : 60} dir="ltr"
                  style={{ ...S.input, ...(f === 'storage_well' ? { ...S.mono, textTransform: 'uppercase' } : {}) }}
                  title={t(`storage.tip.${f}`)} data-testid={`storage-${f}`} />
              </label>
            ))}
            <label style={{ ...S.label, flex: '1 1 90px' }}>
              {t('storage.copiesLabel')}
              <input type="number" min="0" max="100000" value={form.copies_count} onChange={set('copies_count')}
                style={S.input} title={t('storage.tip.copies')} data-testid="storage-copies" />
            </label>
          </div>
          {error && <p style={S.error} role="alert">{error}</p>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" disabled={busy} style={S.btn()} title={t('storage.tip.save')}
              data-testid="storage-save">{busy ? t('saving') : t('storage.save')}</button>
            <button type="button" style={S.ghostBtn} onClick={() => { setEditing(false); setForm(formOf(specimen)); }}
              title={t('tip.cancel')}>{t('cancel')}</button>
          </div>
        </form>
      )}
    </section>
  );
}
